/** Autocomplete dropdown with search history and product suggestions. */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import type { AutocompleteResult, CategorySuggestion } from '../api';

const HISTORY_KEY = 'terra_search_history';
const MAX_HISTORY = 8;

interface AutocompleteProps {
  suggestions: AutocompleteResult[];
  categories: CategorySuggestion[];
  popular: string[];
  query: string;
  selectedIndex: number;
  onSelect: (name: string) => void;
  isLoading: boolean;
}

export function getSearchHistory(): string[] {
  try {
    const stored = localStorage.getItem(HISTORY_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.filter((q) => typeof q === 'string') : [];
  } catch {
    return [];
  }
}

export function addToSearchHistory(query: string) {
  try {
    const history = getSearchHistory().filter((q) => q.toLowerCase() !== query.toLowerCase());
    history.unshift(query);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
  } catch {
    // localStorage unavailable
  }
}

export function clearSearchHistory() {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch {
    // localStorage unavailable
  }
}

export function removeFromSearchHistory(query: string) {
  try {
    const history = getSearchHistory().filter((q) => q.toLowerCase() !== query.toLowerCase());
    if (history.length === 0) {
      localStorage.removeItem(HISTORY_KEY);
    } else {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    }
  } catch {
    // localStorage unavailable
  }
}

function highlightMatch(text: string, query: string): h.JSX.Element {
  if (!query) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </span>
  );
}

export function Autocomplete({ suggestions, categories, popular, query, selectedIndex, onSelect, isLoading }: AutocompleteProps) {
  const [history] = useState(getSearchHistory);

  // Separate part number matches from product suggestions
  const partNumberMatches = suggestions.filter(
    (s) => s.part_number && s.part_number.toLowerCase().includes(query.toLowerCase()),
  );
  const productSuggestions = suggestions.filter(
    (s) => !partNumberMatches.includes(s),
  );

  // Filter history to match current query
  const matchingHistory = history.filter(
    (h) => h.toLowerCase().includes(query.toLowerCase()) && h.toLowerCase() !== query.toLowerCase(),
  );

  // Drop categories with no resolvable landing-page URL — surfacing them as
  // synthetic-search-query links would mislead users into thinking they're
  // navigating to a category page.
  const linkedCategories = (categories || []).filter((c) => !!c.category_url);

  // Build flat list for keyboard navigation indexing
  const allItems: Array<{ type: 'history' | 'part' | 'product' | 'category' | 'popular'; label: string; sub?: string }> = [];

  matchingHistory.forEach((h) => {
    allItems.push({ type: 'history', label: h });
  });
  linkedCategories.forEach((c) => {
    allItems.push({ type: 'category', label: c.name, sub: `${c.count} products` });
  });
  partNumberMatches.forEach((p) => {
    allItems.push({ type: 'part', label: p.name, sub: p.part_number });
  });
  productSuggestions.forEach((p) => {
    allItems.push({ type: 'product', label: p.name, sub: p.category });
  });
  (popular || []).forEach((p) => {
    allItems.push({ type: 'popular', label: p });
  });

  if (allItems.length === 0 && !isLoading) return null;

  let idx = 0;

  return (
    <div class="ts-autocomplete" id="ts-autocomplete-list" role="listbox">
      {/* Search History */}
      {matchingHistory.length > 0 && (
        <div class="ts-autocomplete-section">
          <div class="ts-autocomplete-section-title">Recent Searches</div>
          {matchingHistory.map((h) => {
            const i = idx++;
            return (
              <button
                key={`h-${h}`}
                class={`ts-autocomplete-item ${i === selectedIndex ? 'ts-autocomplete-item--selected' : ''}`}
                onClick={() => onSelect(h)}
                role="option"
                aria-selected={i === selectedIndex}
              >
                <svg class="ts-autocomplete-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12,6 12,12 16,14" />
                </svg>
                <span class="ts-autocomplete-item-text">{highlightMatch(h, query)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Part Number Matches */}
      {partNumberMatches.length > 0 && (
        <div class="ts-autocomplete-section">
          <div class="ts-autocomplete-section-title">Part Numbers</div>
          {partNumberMatches.map((p) => {
            const i = idx++;
            return (
              <button
                key={`pn-${p.product_id}`}
                class={`ts-autocomplete-item ${i === selectedIndex ? 'ts-autocomplete-item--selected' : ''}`}
                onClick={() => onSelect(p.part_number)}
                role="option"
                aria-selected={i === selectedIndex}
              >
                <svg class="ts-autocomplete-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 7V4h16v3" />
                  <path d="M9 20h6" />
                  <path d="M12 4v16" />
                </svg>
                <div class="ts-autocomplete-item-content">
                  <span class="ts-autocomplete-item-text ts-autocomplete-item-text--mono">
                    {highlightMatch(p.part_number, query)}
                  </span>
                  <span class="ts-autocomplete-item-sub">{p.name}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Category Suggestions — anchors that link straight to the real
          category landing page (e.g. /cleanroom-equipment.php).  Categories
          without a resolvable URL are filtered out above so we never present
          a synthetic search query as a category link. */}
      {linkedCategories.length > 0 && (
        <div class="ts-autocomplete-section">
          <div class="ts-autocomplete-section-title">Categories</div>
          {linkedCategories.map((c) => {
            const i = idx++;
            return (
              <a
                key={`cat-${c.name}`}
                href={c.category_url}
                class={`ts-autocomplete-item ${i === selectedIndex ? 'ts-autocomplete-item--selected' : ''}`}
                role="option"
                aria-selected={i === selectedIndex}
              >
                <svg class="ts-autocomplete-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                </svg>
                <div class="ts-autocomplete-item-content">
                  <span class="ts-autocomplete-item-text">{highlightMatch(c.name, query)}</span>
                  <span class="ts-autocomplete-item-sub">{c.count} products</span>
                </div>
              </a>
            );
          })}
        </div>
      )}

      {/* Product Suggestions */}
      {productSuggestions.length > 0 && (
        <div class="ts-autocomplete-section">
          <div class="ts-autocomplete-section-title">Products</div>
          {productSuggestions.map((p) => {
            const i = idx++;
            return (
              <button
                key={`prod-${p.product_id}`}
                class={`ts-autocomplete-item ${i === selectedIndex ? 'ts-autocomplete-item--selected' : ''}`}
                onClick={() => onSelect(p.name)}
                role="option"
                aria-selected={i === selectedIndex}
              >
                <svg class="ts-autocomplete-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0022 16z" />
                </svg>
                <div class="ts-autocomplete-item-content">
                  <span class="ts-autocomplete-item-text">{highlightMatch(p.name, query)}</span>
                  {p.category && <span class="ts-autocomplete-item-sub">{p.category}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Popular Searches */}
      {(popular || []).length > 0 && suggestions.length === 0 && (
        <div class="ts-autocomplete-section">
          <div class="ts-autocomplete-section-title">Trending Searches</div>
          {popular.map((p) => {
            const i = idx++;
            return (
              <button
                key={`pop-${p}`}
                class={`ts-autocomplete-item ${i === selectedIndex ? 'ts-autocomplete-item--selected' : ''}`}
                onClick={() => onSelect(p)}
                role="option"
                aria-selected={i === selectedIndex}
              >
                <svg class="ts-autocomplete-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
                <span class="ts-autocomplete-item-text">{p}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Loading state */}
      {isLoading && allItems.length === 0 && (
        <div class="ts-autocomplete-loading">Searching...</div>
      )}
    </div>
  );
}
